<script setup lang="ts">
import ChatMessageTemplateTable, {
  type ChatMessageTemplateTableForm,
} from './ChatMessageTemplateTable.vue'
import { computed, ref, watch } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import { captureException as newrelicCaptureException } from '@/newrelic'
import {
  useSettingsPageUpdateChatMessageTemplateMutation,
  useSettingsPageGetMessageTemplateByIdQuery,
} from './SettingsPageChatMessageTemplateDetailView.generated'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const route = useRoute('chatMessageTemplateDetail')
const chatMessageTemplateGuid = route.params.chatMessageTemplateGuid

const { result, error } = useSettingsPageGetMessageTemplateByIdQuery({
  guid: chatMessageTemplateGuid,
})

const isCompleted = ref(false)

const { mutate: updateChatMessageTemplate } =
  useSettingsPageUpdateChatMessageTemplateMutation({})

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'chatMessageTemplateNew') {
    next()
    return
  }
  if (to.name === 'chatMessageTemplate' && !isCompleted.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})

watch(error, e => {
  if (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
})

const alert = useAlert()

const router = useRouter()

const chatMessageTemplate = computed(() => {
  if (!result.value) {
    return undefined
  }

  const { chatMessageTemplate } = result.value
  return chatMessageTemplate
})

const form = computed<ChatMessageTemplateTableForm>(() => {
  if (!chatMessageTemplate.value) {
    return {
      label: '',
      body: '',
      selections: [],
    }
  }

  return {
    label: chatMessageTemplate.value.label,
    body: chatMessageTemplate.value.body,
    selections: chatMessageTemplate.value.selections,
  }
})

const handleSave = async (input: ChatMessageTemplateTableForm) => {
  try {
    await updateChatMessageTemplate({
      input: {
        guid: chatMessageTemplateGuid,
        label: input.label,
        body: input.body,
        selections: input.selections,
      },
    })
    alert.success('チャットメッセージテンプレートを更新しました')
    // eslint-disable-next-line fp/no-mutating-methods
    await router.push({
      name: 'chatMessageTemplate',
    })
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}
</script>

<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>チャットメッセージテンプレート 編集</template>
    </SettingsPagePanelHeader>
    <ChatMessageTemplateTable v-model:form="form" @save="handleSave" />
  </SettingsPagePanel>
</template>
